import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, VIEW, EDIT, DANGER, SUCCESS,
} from "const"
import { projectsRoute } from "client/parts/routes"
import { MtProject, MtProjectCreate } from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
    deepCopy,
} from "client/utils"
import {
    StoreType,
    ProjectActiveModules,
    ProjectUserList,
    ProjectValidation,

    SaveProject,
    GetMaxProjects,
    GetMaxProjectUsers,

    closeModal,
    setProjectEdit,
    setProjectReloadList,
    setProjectReloadView,
    setProjectReloadEdit,
    setProjectUserList,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    CKEditor,
    FileLoader,
    Delimiter,
    UserDropdown,
    UserDropdownList,
    Button,
    SpinnerIcon,
} from "client/components"

import ProjectModules from "./modules"

interface EditProjectProps {
    project: MtProject
}

let isMounted: boolean

const Form = ({
    project,
}: EditProjectProps) => {
    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const userSettings = useSelector((state: StoreType) => state.system.userSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const {
        defaultPublicId,
        defaultName,
        defaultDescription,
        defaultModules,
    } = userSettings.project

    const [publicId, setPublicId] = useInput(project.id ? project.publicId : defaultPublicId)
    const [name, setName] = useInput(project.id ? project.name : defaultName)
    const [documentId, setDocumentId] = useInput(project.documentId)

    const [description, setDescription] = useState(project.id ? project.description : defaultDescription)
    const [logoId, setLogoId] = useState(project.logoId)
    const [logo, setLogo] = useState(project.logo)

    const [owner, setOwner] = useState(null)
    const [modules, setModules] = useState<ProjectActiveModules>({})
    const [participants, setParticipants] = useState<readonly number[]>([])

    const [validation, setValidation] = useState<ProjectValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        if (project.id) {
            setOwner(project.owner)
            if (project.modules) {
                setModules(JSON.parse(project.modules))
            }
            if (projectUserList && projectUserList[project.id]) {
                setParticipants(projectUserList[project.id])
            }
        } else {
            setOwner(userData.userId)
            setModules(defaultModules)
            setParticipants([userData.userId])
        }

        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = project.id
            ? `${projectsRoute}/${VIEW}/${project.id}`
            : projectsRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newProjectData: MtProjectCreate = {
            accountId: project.accountId || userData.accountId,
            publicId,
            name,
            description,
            documentId,
            logoId,
            owner,
            modules: JSON.stringify(modules),
        }

        const maxProjects = GetMaxProjects(packageSettings)
        SaveProject(
            project.id,
            newProjectData,
            participants,
            maxProjects,
            accountSettings,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newProjectId) replaceHistoryState(`${projectsRoute}/${EDIT}/${res.newProjectId}`)
            }
            if (res.validation) return

            dispatch(setProjectEdit({
                id: res.newProjectId || project.id,
                logo,
                ...newProjectData,
            }))

            const newProjectUserList: ProjectUserList = { [res.newProjectId || project.id]: participants }
            dispatch(setProjectUserList(newProjectUserList))

            dispatch(setProjectReloadList(false))
            dispatch(setProjectReloadView(false))
            setTimeout(() => {
                dispatch(setProjectReloadList(true))
                dispatch(setProjectReloadView(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setProjectReloadEdit(false))
                setTimeout(() => {
                    dispatch(setProjectReloadEdit(true))
                }, 0)
            }
        })
    }

    const accountUsers = (accountUserList && accountUserList[userData.accountId]) || []

    // max number of team members
    const {
        feedback,
        maxNumberReached,
        maxNumberExceeded,
    } = GetMaxProjectUsers(packageSettings, participants.length, lang)

    return (
        <PageForm>
            <Row>
                <Col xs={12} md={4}>
                    <FormGroup>
                        <Label>{lang.publicId}</Label>
                        <Input type="text" value={publicId} onChange={setPublicId} />
                        <Feedback>{validation && validation.publicId}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12} md={8}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.documentId}</Label>
                        <Input type="text" value={documentId} onChange={setDocumentId} />
                        <Feedback>{validation && validation.documentId}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.image}</Label>
                        <FileLoader
                            value={logo}
                            callback={(file) => {
                                setLogoId(file.id)
                                setLogo(file)
                            }}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <CKEditor
                            data={description}
                            onChange={setDescription}
                        />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <Delimiter />

                    <ProjectModules
                        activeModules={modules}
                        onChange={setModules}
                        title={lang.assignedModules}
                    />
                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.owner}</Label>
                        <UserDropdown
                            selectedId={owner}
                            listIds={accountUsers}
                            title={lang.changeOwner}
                            showDelete={false}
                            showProjectStatus={false}
                            onChange={(userId) => {
                                if (participants && !participants.includes(userId)) {
                                    const participantsCopy: number[] = deepCopy(participants)
                                    participantsCopy.push(userId)
                                    setParticipants(participantsCopy)
                                }
                                setOwner(userId)
                            }}
                        />
                    </FormGroup>
                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.participants}</Label>
                        <UserDropdownList
                            selectedIds={participants}
                            protectedIds={[owner]}
                            listIds={accountUsers}
                            title={lang.addParticipant}
                            dropdownDisabled={maxNumberReached}
                            showProjectStatus={false}
                            onDelete={setParticipants}
                            onAdd={setParticipants}
                        />
                        <Feedback variant={maxNumberExceeded ? DANGER : SUCCESS}>
                            {feedback}
                        </Feedback>
                    </FormGroup>
                    <Delimiter />
                </Col>
                <Col xs={12}>
                    <Button
                        variant="success"
                        row
                        disabled={maxNumberExceeded}
                        onClick={() => onSave(1)}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button
                        variant="success"
                        row
                        disabled={maxNumberExceeded}
                        onClick={() => onSave(2)}
                    >
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
